import type {
  RateAndReviewStatus,
  ReviewFormData,
  ReviewOption,
  ReviewableProduct,
} from '@iheartjane/menu';
import {
  INITIAL_REVIEW_FORM_DATA,
  RateAndReviewFormModal,
  RateAndReviewWizardModal,
} from '@iheartjane/menu';
import { useEffect, useRef, useState } from 'react';

import { useFetcherWithMutation } from '@jane/bloom/hooks/useFetcherWithMutation';
import { useLeaveReviewAbTest } from '@jane/bloom/hooks/useLeaveReviewAbTest';
import type { CreateProductReviewErrors } from '@jane/bloom/routes/api.product-reviews/types';
import type { LeaveReviewSource } from '@jane/bloom/services/tracking/Events';
import {
  trackLoadedWriteProductReviewForm,
  trackReviewedProduct,
} from '@jane/bloom/services/tracking/track';

import type { LeaveReviewProduct } from './types';

type ReviewSubmission = {
  activities: number[];
  comment: string;
  feelings: number[];
  productId: number;
  rating: number;
};

export interface LeaveReviewModalProps {
  activityOptions: ReviewOption[];
  feelingOptions: ReviewOption[];
  isMobile: boolean;
  moreToReview: LeaveReviewProduct[];
  onClose: (reviewedIds: number[]) => void;
  product: LeaveReviewProduct;
  source: LeaveReviewSource;
}

const labelsFor = (ids: number[], options: ReviewOption[]) =>
  options.filter(({ id }) => ids.includes(id)).map(({ label }) => label);

const reviewErrors = (errors: unknown) =>
  (errors ?? {}) as Partial<CreateProductReviewErrors>;

const isRetryableReason = (
  reason: CreateProductReviewErrors['reason'] | undefined
) => reason === undefined || reason === 'failed';

export const LeaveReviewModal = ({
  activityOptions,
  feelingOptions,
  isMobile,
  moreToReview,
  onClose,
  product: initialProduct,
  source,
}: LeaveReviewModalProps) => {
  const showWizard = useLeaveReviewAbTest(true);
  const [products] = useState(() =>
    moreToReview.some(({ id }) => id === initialProduct.id)
      ? moreToReview
      : [initialProduct, ...moreToReview]
  );
  const [product, setProduct] = useState(initialProduct);
  const [doneIds, setDoneIds] = useState<number[]>([]);
  const [drafts, setDrafts] = useState<Record<number, ReviewFormData>>({});
  const [status, setStatus] = useState<RateAndReviewStatus>('editing');
  const [isRetryable, setIsRetryable] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string>();

  const openedIdsRef = useRef(new Set([initialProduct.id]));
  const reviewedIdsRef = useRef<number[]>([]);

  const value = drafts[product.id] ?? INITIAL_REVIEW_FORM_DATA;
  const pending = products.filter(({ id }) => !doneIds.includes(id));
  const others = pending.filter(({ id }) => id !== product.id);

  useEffect(() => {
    trackLoadedWriteProductReviewForm(source);
  }, [source]);

  const { isPending, mutate } = useFetcherWithMutation<ReviewSubmission, null>({
    action: '/api/product-reviews',
  });

  const handleSubmit = () => {
    const submitted = { productId: product.id, ...value };
    mutate(submitted, {
      onError: (errors) => {
        const { message, reason } = reviewErrors(errors);
        const retryable = isRetryableReason(reason);
        if (!retryable) setDoneIds((ids) => [...ids, product.id]);
        if (reason === 'already_reviewed') {
          reviewedIdsRef.current.push(product.id);
        }
        setIsRetryable(retryable);
        setErrorMessage(message);
        setStatus('failed');
      },
      onSuccess: () => {
        reviewedIdsRef.current.push(product.id);
        setDoneIds((ids) => [...ids, product.id]);
        setStatus('submitted');
        trackReviewedProduct({
          activities: labelsFor(submitted.activities, activityOptions),
          feelings: labelsFor(submitted.feelings, feelingOptions),
          hasComment: submitted.comment.trim() !== '',
          productId: product.id,
          productName: product.name,
          rating: submitted.rating,
          source,
        });
      },
    });
  };

  const handleReviewProduct = ({ id }: ReviewableProduct) => {
    const next = pending.find((candidate) => candidate.id === id);
    if (!next) return;

    setIsRetryable(true);
    setErrorMessage(undefined);
    setProduct(next);
    setStatus('editing');
    if (!openedIdsRef.current.has(next.id)) {
      openedIdsRef.current.add(next.id);
      trackLoadedWriteProductReviewForm(source);
    }
  };

  const handleChange = (data: ReviewFormData) =>
    setDrafts({ ...drafts, [product.id]: data });

  const Variant = showWizard
    ? RateAndReviewWizardModal
    : RateAndReviewFormModal;

  return (
    <Variant
      activityOptions={activityOptions}
      brand={product.brand}
      category={product.category}
      errorMessage={errorMessage}
      feelingOptions={feelingOptions}
      imageUrl={product.imageUrl}
      isMobile={isMobile}
      isRetryable={isRetryable}
      isSubmitting={isPending}
      moreToReview={status === 'choosing' ? pending : others}
      onBack={others.length > 0 ? () => setStatus('choosing') : undefined}
      onChange={handleChange}
      onRequestClose={() => onClose(reviewedIdsRef.current)}
      onReviewProduct={handleReviewProduct}
      onSubmit={handleSubmit}
      open
      productName={product.name}
      status={status}
      value={value}
    />
  );
};
